import { useAtomValue } from 'jotai';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@crossnokaye/ux-common';
import {
  ActionBarActions,
  ActionBarCancelButton,
  ActionBarRoot,
  ActionBarSaveButton,
  ActionBarStatus,
} from '@crossnokaye/ux-common/ckds';

import { SettingsSaveDialog } from '@/components/settings/settings-save-dialog';
import { DetailsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { collectFieldErrors } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form-error.helpers';
import { drawerIsOpenAtom } from '@/features/equipment-browser/equipment-drawer-loader/state/drawer.state';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { useDetailsSettingsSave } from '../hooks/use-details-settings-save';

type EquipmentDetailsFormSaveBarProps = {
  onDiscard: () => Promise<void>;
};

/**
 * EquipmentDetailsFormSaveBar
 *
 * Sticky save bar that appears when the form has unsaved changes and the details drawer is open.
 * Uses the shared SettingsSaveDialog for all settings — scalar settings show
 * field-level before/after diffs, while sequencer config and defrost schedule
 * show descriptive messages.
 */
export const EquipmentDetailsFormSaveBar: React.FC<EquipmentDetailsFormSaveBarProps> = ({
  onDiscard,
}) => {
  const { formState } = useFormContext<DetailsFormData>();
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  const {
    settingsSave,
    groups,
    changeCount,
    handleDiscard,
    openDialog: openSaveDialog,
  } = useDetailsSettingsSave();

  const isDrawerOpen = useAtomValue(drawerIsOpenAtom);

  const { isDirty, isSubmitting, isValid, errors } = formState;
  const disableSave = !isDirty || !isValid || isSubmitting || disabledByPermissions;

  // We purposefully did not memoize collectFieldErrors to ensure we capture changes to the errors object
  const validationErrors = collectFieldErrors(errors);

  const saveButtonTooltipContent = useMemo(() => {
    if (disabledByPermissions && tooltipText) {
      return tooltipText;
    }
    if (!isValid && validationErrors.length > 0) {
      return (
        <ul className="list-disc pl-4 text-left text-ckds-base-default max-h-24 max-w-xs overflow-y-auto">
          {validationErrors.map(({ field, message }, index) => (
            <li key={`${index}-${field}-${message}`}>
              <span className="font-medium">{field}:</span> {message}
            </li>
          ))}
        </ul>
      );
    }
    return undefined;
  }, [disabledByPermissions, isValid, tooltipText, validationErrors]);

  const openDialog = useCallback(() => {
    if (!disableSave) {
      openSaveDialog();
    }
  }, [disableSave, openSaveDialog]);

  const handleClose = useCallback(() => {
    settingsSave.close();
  }, [settingsSave]);

  const isSaving = isSubmitting || settingsSave.hasPendingSave;
  const isSaveBarOpen = isDirty && isDrawerOpen;

  return (
    <>
      <ActionBarRoot open={isSaveBarOpen} className="relative">
        <ActionBarStatus
          statusText={
            isSaving
              ? 'Saving equipment updates\u2026'
              : `${changeCount} setting${changeCount === 1 ? '' : 's'} changed`
          }
        />
        <ActionBarActions>
          <ActionBarCancelButton onCancel={() => onDiscard()} isSubmitting={isSaving}>
            Cancel
          </ActionBarCancelButton>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="inline-flex" data-testid="equipment-details-save-tooltip-trigger">
                <ActionBarSaveButton
                  type="button"
                  onClick={openDialog}
                  disabled={disableSave}
                  isSubmitting={isSaving}
                >
                  {`Review & Save ${changeCount} Change${changeCount === 1 ? '' : 's'}`}
                </ActionBarSaveButton>
              </span>
            </TooltipTrigger>
            {saveButtonTooltipContent && (
              <TooltipPortal>
                <TooltipContent side="top" align="center" className="z-50 max-w-xs">
                  {saveButtonTooltipContent}
                </TooltipContent>
              </TooltipPortal>
            )}
          </Tooltip>
        </ActionBarActions>
      </ActionBarRoot>

      <SettingsSaveDialog
        open={settingsSave.isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        groups={groups}
        saveProgressByChangeId={settingsSave.saveProgressByChangeId}
        hasPendingSave={settingsSave.hasPendingSave}
        saveComplete={settingsSave.saveComplete}
        progressStats={settingsSave.progressStats}
        onDiscard={handleDiscard}
        onConfirm={() => settingsSave.confirmSave()}
        onCancelPending={settingsSave.cancelPending}
        onClose={handleClose}
      />
    </>
  );
};
